import { useEffect } from 'react'
import { GetServerSidePropsContext, GetServerSideProps } from 'next'
import Cookie from 'cookie-universal'
import { FreeTrial as GuestPassRecipient, Product } from '@mc/api'
import { ONE_YEAR, REFERRAL_TOKEN_COOKIE_KEY } from '@mc/constants'
import { StaticDataProvider } from '@mc/static-data-react'
import { EventTypes } from '@mc/track-event'
import { FAQ } from 'components/FAQ'
import { RegularFooter } from 'components/Footer/RegularFooter'
import { CoreNavigation } from 'components/Navigation/consumer'
import {
  getV2Categories,
  getDefaultProduct,
  getFreeTrialByToken,
} from 'libs/server/api'
import { getPageData } from 'libs/server/page-data'
import GuestPassBanner from '../../components/GuestPassBanner'
import { StickyFooter } from '../../components/StickyFooter'
import { RecipientHero } from './components/Hero'
import { Meta } from './components/Meta'
import { SubscriptionExperience } from './components/SubscriptionExperience'
import { trackExclusiveGuestPassPageViewed } from './utils/events'

interface ExclusiveGuestPassProps {
  annualPass: Product
  trial: GuestPassRecipient
  referralToken?: string
}

const setReferralCookie = (referralToken?: string) => {
  const cookies = Cookie()
  const cookiesToken = cookies.get(REFERRAL_TOKEN_COOKIE_KEY)

  if (referralToken !== cookiesToken) {
    cookies.set(REFERRAL_TOKEN_COOKIE_KEY, referralToken, { maxAge: ONE_YEAR })
  }
}

const ExclusiveGuestPass = ({
  annualPass,
  trial,
  referralToken,
}: ExclusiveGuestPassProps) => {
  // instead of setting the analyticsLabel in the pageSettings
  // we are firing the page event here because we need to send the advocate data
  useEffect(() => {
    trackExclusiveGuestPassPageViewed(trial)
    setReferralCookie(referralToken)
  }, [trial, referralToken])

  return (
    <StaticDataProvider
      value={{
        trial,
        annualPass,
      }}
    >
      <Meta />
      <GuestPassBanner />
      <RecipientHero />
      <SubscriptionExperience />
      <FAQ
        className='container'
        sections={['general', 'guest-pass', 'pricing']}
        eventType={EventTypes.RECIPIENT_GUEST_PASS_CLICK}
        eventLocation='exclusive-guest-pass-faq'
      />
      <StickyFooter />
    </StaticDataProvider>
  )
}

ExclusiveGuestPass.pageSettings = {
  title: 'Exclusive Guest Pass',
  navigation: CoreNavigation,
  footer: RegularFooter,
}

export const getServerSideProps: GetServerSideProps = async (
  ctx: GetServerSidePropsContext,
) => {
  const {
    req: {
      cookies: { referral_token: cookiesReferralToken },
    },
  } = ctx

  const paramsReferralToken = ctx.query?.referral_token

  const referralToken = (
    paramsReferralToken || cookiesReferralToken
  )?.toString()

  const redirect = {
    redirect: {
      destination: '/',
      permanent: false,
    },
  }

  if (referralToken) {
    const trial = await getFreeTrialByToken(ctx, { referralToken })

    if (trial?.eligible && !trial?.partnership) {
      return getPageData({
        nonCriticalPromises: {
          categories: getV2Categories(ctx),
          annualPass: getDefaultProduct(ctx),
        },
        propsMapper: ({ ...restProps }) => ({
          trial,
          referralToken,
          ...restProps,
        }),
      })
    }
    return redirect
  }

  return redirect
}

export default ExclusiveGuestPass
